import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { ROLE_LABELS, ROLE_COLORS } from "@/lib/rbac";
import { Button } from "@/components/ui/button";
import { History } from "lucide-react";

const EMPTY_FILTERS = { username: "", start: "", end: "", status: "all" };
const SEL = "h-8 text-xs border border-slate-200 rounded-md px-2 bg-white";

function statusLabel(e) {
  if (e.success !== false) return "Success";
  if (e.reason === "locked") return "Locked";
  if (e.reason === "session_conflict") return "Session Conflict";
  return "Failed";
}

function Filters({ users, filters, onChange }) {
  const set = (k) => (e) => onChange((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div className="flex flex-wrap items-end gap-2 mb-4" data-testid="login-activity-filters">
      <div>
        <label className="block text-[10px] uppercase text-slate-400 mb-1">User</label>
        <select value={filters.username} onChange={set("username")} className={SEL} data-testid="la-filter-user">
          <option value="">All Users</option>
          {users.map((u) => (<option key={u.id} value={u.username}>@{u.username}</option>))}
        </select>
      </div>
      <div>
        <label className="block text-[10px] uppercase text-slate-400 mb-1">From</label>
        <input type="date" value={filters.start} onChange={set("start")} className={SEL} data-testid="la-filter-start" />
      </div>
      <div>
        <label className="block text-[10px] uppercase text-slate-400 mb-1">To</label>
        <input type="date" value={filters.end} onChange={set("end")} className={SEL} data-testid="la-filter-end" />
      </div>
      <div>
        <label className="block text-[10px] uppercase text-slate-400 mb-1">Status</label>
        <select value={filters.status} onChange={set("status")} className={SEL} data-testid="la-filter-status">
          <option value="all">All</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
        </select>
      </div>
      <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={() => onChange(EMPTY_FILTERS)} data-testid="la-filter-clear">Clear</Button>
    </div>
  );
}

// Sign-in attempts across all users; `refreshKey` re-fetches after unlocks/dismissals.
export default function LoginActivity({ users, refreshKey }) {
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") p.set(k, v); });
    const qs = p.toString() ? `?${p}` : "";
    try { const res = await api.get(`/users/login-activity${qs}`); setEvents(res?.events || []); }
    catch (e) { logger.error("Failed to load login activity:", e); }
  }, [filters]);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="xl:col-span-3 bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="login-activity">
      <div className="flex items-center gap-2 mb-3">
        <History className="h-4 w-4 text-slate-500" />
        <h3 className="font-display text-base font-semibold text-slate-900">Login Activity</h3>
        <span className="text-xs text-slate-400">— sign-in attempts across all users</span>
      </div>
      <Filters users={users} filters={filters} onChange={setFilters} />
      {events.length === 0 ? (
        <p className="text-xs text-slate-400" data-testid="login-activity-empty">No matching login activity.</p>
      ) : (
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="py-2 font-semibold">User</th>
                <th className="py-2 font-semibold">Username</th>
                <th className="py-2 font-semibold">Role</th>
                <th className="py-2 font-semibold">Status</th>
                <th className="py-2 font-semibold text-right">Signed In</th>
              </tr>
            </thead>
            <tbody data-testid="login-activity-list">
              {events.map((e, i) => {
                const ok = e.success !== false;
                return (
                  <tr key={e.id || i} className="border-b border-slate-50" data-testid="login-activity-row">
                    <td className="py-2 text-slate-700 font-medium">{e.name || e.username}</td>
                    <td className="py-2 text-slate-500 font-mono-data">@{e.username}</td>
                    <td className="py-2">
                      {e.role ? <span className={`text-[10px] font-mono-data uppercase px-2 py-0.5 rounded ${ROLE_COLORS[e.role] || "bg-slate-100 text-slate-600"}`}>{ROLE_LABELS[e.role] || e.role}</span> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-2">
                      <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${ok ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`} data-testid="login-status">{statusLabel(e)}</span>
                    </td>
                    <td className="py-2 text-slate-500 text-right">{e.at ? new Date(e.at).toLocaleString("en-IN") : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
